<template>
  <div class="account-info">
    <!-- Logo上传 -->
    <div class="logo-section">
      <div class="logo-label">公司Logo</div>
      <var-uploader v-model="files" :maxlength="1" :maxsize="2 * 1024 * 1024" @after-read="handleAfterRead"
        @oversize="onOversize" :disabled="!isAdmin">
        <template #extra-message>💡 建议尺寸 200x200，支持 PNG/JPG 格式，大小不超过 2MB</template>
      </var-uploader>
    </div>

    <var-divider style="margin: 16px 0;" />

    <!-- 公司名称 -->
    <var-input v-model="form.name" label="公司名称" placeholder="请输入公司名称" :disabled="!isAdmin" />
    <span v-if="!isAdmin" style="color:#909399;font-size:12px;margin-top:4px;">
      💡 只有管理员可以修改公司信息
    </span>

    <template v-if="isAdmin && $isDesktop">
      <var-input v-model="form.address" label="公司地址" placeholder="请输入公司地址" />
      <var-input v-model="form.phone" label="联系电话" placeholder="请输入联系电话" />
      <var-input v-model="form.contact_person" label="联系人" placeholder="请输入联系人" />
      <var-input v-model="form.contact_phone" label="联系人电话" placeholder="请输入联系人电话" />
    </template>

    <var-divider style="margin: 16px 0;" />

    <div class="logout-section">
      <!-- ✅ 左侧：当前版本号 -->
      <div class="version-info">
        <var-icon name="information" size="16" color="#909399" />
        <span class="version-text">当前版本：{{ currentVersion || '未知' }}</span>
      </div>

      <div class="action-buttons">
        <!-- ✅ Web 端：显示 APK 下载按钮 -->
        <var-button v-if="!isNativeApp" size="small" type="success" @click="handleDownloadApk">
          下载 App
        </var-button>

        <!-- ✅ 原生 App 端：显示检测更新按钮 -->
        <var-button v-if="isNativeApp" size="small" type="primary" :loading="checkingUpdate" @click="handleCheckUpdate">
          检测更新
        </var-button>

        <var-button size="small" type="danger" @click="handleLogout">
          退出登录
        </var-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { Snackbar, Dialog } from '@varlet/ui'
import { useRouter } from 'vue-router'
import { useAppStore } from '@/stores'
import { companyApi } from '@/api'
import { useUploader } from '@/composables/useUploader'

// ✅ Capgo 热更新
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { Capacitor } from '@capacitor/core'
import { mobileApi } from '@/api'

const router = useRouter()
const appStore = useAppStore()

// ✅ APK 下载地址（按你服务器实际路径改）
const APK_DOWNLOAD_URL = 'https://www.sshoes.cn/apk/app-debug.apk'

const form = reactive({
  id: null,
  name: '',
  address: '',
  phone: '',
  contact_person: '',
  contact_phone: '',
  logo: ''
})

const {
  files,
  handleAfterRead,
  onOversize,
  uploadFiles,
  getImageUrls,
  hasPending,
  setExistingFiles,
  clearFiles
} = useUploader()

// ===== 监听文件变化，同步到 form.logo =====
watch(files, () => {
  const urls = getImageUrls()
  form.logo = urls.length > 0 ? urls[0] : ''
}, { deep: true })

const isAdmin = computed(() => {
  return appStore.currentUserInfo?.role === 'admin'
})

// ✅ 是否为原生 App 环境
const isNativeApp = computed(() => Capacitor.isNativePlatform())

// ✅ 检测更新 loading 状态
const checkingUpdate = ref(false)

// ✅ 当前版本号
const currentVersion = ref('')

async function loadCurrentVersion() {
  if (Capacitor.isNativePlatform()) {
    try {
      const current = await CapacitorUpdater.current()
      currentVersion.value = current?.bundle?.version || ''
    } catch (err) {
      console.warn('[Capgo] 获取当前版本失败:', err)
    }
  } else {
    currentVersion.value = import.meta.env.VITE_APP_VERSION || 'web'
  }
}

/**
 * Web 端：下载 APK
 * 用 window.open 触发浏览器下载，比 <a download> 兼容性更好
 */
function handleDownloadApk() {
  // 可选：加个二次确认
  Dialog({
    title: '下载 App',
    message: '将下载 Android 安装包（APK），是否继续？',
    onConfirm: () => {
      window.open(APK_DOWNLOAD_URL, '_blank')
      Snackbar.success('已开始下载，请在通知栏查看进度')
    },
  })
}

function loadCompanyInfo() {
  const company = appStore.currentUserInfo?.company
  if (company) {
    Object.assign(form, {
      id: company.id || null,
      name: company.name || '',
      address: company.address || '',
      phone: company.phone || '',
      contact_person: company.contact_person || '',
      contact_phone: company.contact_phone || '',
      logo: company.logo || ''
    })
    setExistingFiles(company.logo ? [company.logo] : [])
  }
}

onMounted(() => {
  loadCompanyInfo()
  loadCurrentVersion()
})

async function saveCompany() {
  if (!isAdmin.value) {
    Snackbar.warning('只有管理员可以修改公司信息')
    return false
  }

  if (!form.name || !form.name.trim()) {
    Snackbar.warning('公司名称不能为空')
    return false
  }

  try {
    let logoUrl = form.logo || ''
    if (hasPending()) {
      const urls = await uploadFiles('logo')
      logoUrl = urls.length > 0 ? urls[0] : ''
    } else {
      const urls = getImageUrls()
      logoUrl = urls.length > 0 ? urls[0] : ''
    }

    const data = await companyApi.update({
      name: form.name.trim(),
      address: form.address || '',
      phone: form.phone || '',
      contact_person: form.contact_person || '',
      contact_phone: form.contact_phone || '',
      logo: logoUrl || null
    })

    if (data?.success) {
      if (appStore.currentUserInfo) {
        appStore.currentUserInfo.company = {
          ...appStore.currentUserInfo.company,
          ...data.data
        }
      }
      Snackbar.success('公司信息更新成功')
      return true
    } else {
      Snackbar.error(data?.msg || '更新失败')
      return false
    }
  } catch (err) {
    Snackbar.error('更新公司信息失败: ' + err.message)
    return false
  }
}

async function handleCheckUpdate() {
  if (!Capacitor.isNativePlatform()) {
    Snackbar.info('当前为网页环境，无需检测更新')
    return
  }

  if (checkingUpdate.value) return

  checkingUpdate.value = true
  try {
    const res = await mobileApi.getUpdateInfo()

    if (!res || res.success === false) {
      Snackbar.warning(res?.msg || '未获取到更新信息')
      return
    }

    const info = res.data ?? res

    if (!info.url || !info.version) {
      Snackbar.warning('服务器未返回有效的更新信息')
      return
    }

    const current = await CapacitorUpdater.current()
    if (current?.bundle?.version === info.version) {
      Snackbar.success('当前已是最新版本')
      return
    }

    Dialog({
      title: '发现新版本',
      message: `是否立即下载并更新到 ${info.version}？更新完成后应用会自动重启。`,
      onConfirm: async () => {
        try {
          const bundle = await CapacitorUpdater.download({
            url: info.url,
            version: info.version,
            checksum: info.checksum || undefined
          })
          await CapacitorUpdater.set({ id: bundle.id })
        } catch (err) {
          console.error('[Capgo] 更新失败:', err)
          Snackbar.error('更新失败: ' + (err.message || err))
        }
      },
    })
  } catch (err) {
    console.error('[Capgo] 检测更新失败:', err)
    Snackbar.error('检测更新失败: ' + (err.message || err))
  } finally {
    checkingUpdate.value = false
  }
}

async function handleLogout() {
  Dialog({
    title: '确认退出',
    message: '确定要退出登录吗？',
    onConfirm: async () => {
      try {
        appStore.logout()
        await router.replace('/login')
        Snackbar.success('已退出登录')
      } catch (err) {
        Snackbar.error('退出失败: ' + err.message)
      }
    },
  })
}

defineExpose({ saveCompany })
</script>

<style scoped>
.account-info {
  padding: 4px 0;
}

.logo-section {
  margin-bottom: 12px;
}

.logo-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--var-text-primary);
  margin-bottom: 8px;
}

.logout-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 4px;
  background: #fafafa;
}

.version-info {
  display: flex;
  align-items: center;
  gap: 6px;
}

.version-text {
  font-size: 12px;
  color: var(--var-text-secondary);
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .version-info {
    justify-content: center;
  }

  .action-buttons {
    justify-content: flex-end;
  }
}
</style>